import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import { toast } from "react-toastify";
import type { LatLng, LatLngBounds, Map } from "leaflet";
import * as Sentry from "@sentry/react";
import {
  createBoundsFromCenterMeters,
  getPrintLayout,
  type MapPrintOptions,
} from "../utils/printLayout";
import { applyExportVisibility } from "../utils/mapExportVisibility";
import {
  waitForMapReady,
  type WaitForMapReadyProgress,
} from "../utils/waitForMapReady";
import { createMapCanvas } from "../utils/mapCanvas";
import {
  composeImageCanvas,
  composePrintCanvas,
  cropCanvasToBounds,
} from "../utils/composeExportCanvas";
import { checkCanvasSizeExceeded } from "../utils/canvasSizeCheck";
import {
  enlargeMapContainerOffscreen,
  getPrintCaptureSize,
  restoreMapContainerSize,
  type MapContainerSizeState,
} from "../utils/mapContainerSize";
import { getGlMap } from "../utils/leafletMaplibreInternals";
import {
  beginZenrinTileFailureWindow,
  getZenrinTileFailureCount,
} from "../utils/zenrinTileFailureWindow";
import {
  createPrintStyleSession,
  type PrintStyleSession,
} from "../utils/printStyleSession";
import {
  createPrintRenderContext,
  getBoundsZoomForSize,
  type PrintRenderContext,
} from "../utils/printRenderScale";
import {
  createLeafletPrintAdapter,
  DRAWN_SHAPES_PANE_NAME,
} from "../utils/leafletPrintAdapter";
import { TileLayerWithHeaders } from "../components/ZenrinTileLayer";

/**
 * Zenrin タイル取得が失敗した場合、Leaflet 側ではそのタイルが「読み込み済み」
 * として残り、同じ座標を再リクエストできなくなる（img.style.display="none" の
 * 状態で done() 済みとして扱われるため）。
 * 失敗を検知した直後にこの関数で対象レイヤーを redraw すると、キャッシュ済み
 * タイルが破棄され、次の renderMapExportCanvas / handleDownload* 呼び出しで
 * 再フェッチが行われる。
 */
const reloadZenrinTileLayers = (mapInstance: Map): void => {
  mapInstance.eachLayer((layer) => {
    if (layer instanceof TileLayerWithHeaders) {
      layer.redraw();
    }
  });
};

/**
 * 出力フォーマットの種類
 * - png: PNG画像として出力
 * - pdf: PDF文書として出力
 * - both: PNGとPDFの両方をZIPにまとめて出力
 */
export type MapImageFormat = "png" | "pdf" | "both";
export type MapExportMode = "image" | "print";

/**
 * 地図画像のエクスポート描画オプション。
 * 各描画レイヤーやピン、図形の表示・非表示、およびタイトルや印刷用のレイアウト設定などを保持する。
 */
export interface MapExportRenderOptions {
  /** ダウンロード画像に追加のレイヤーを含めるかどうか */
  includeLayers?: boolean;
  /** ダウンロード画像にピンを含めるかどうか */
  includePins?: boolean;
  /** ダウンロード画像に描画した図形を含めるかどうか */
  includeDrawnShapes?: boolean;
  /** ダウンロード画像に現在地マーカーを含めるかどうか */
  includeCurrentLocation?: boolean;
  /** 成果物に表示する任意タイトル */
  title?: string;
  /** 画像成果物にスケールバーを焼き込むかどうか */
  showScaleBar?: boolean;
  /** 印刷用成果物の設定 */
  printOptions?: MapPrintOptions;
}

/**
 * 解決済みのエクスポート設定オプション。
 * MapExportRenderOptions をベースに、フックの初期設定からデフォルト値が
 * 補完される3つの主要フラグ（includeLayers, includePins, includeDrawnShapes）を
 * 必須型（Required）に変更した型
 */
export type ResolvedMapExportOptions = MapExportRenderOptions &
  Required<
    Pick<
      MapExportRenderOptions,
      "includeLayers" | "includePins" | "includeDrawnShapes"
    >
  >;

/**
 * 2つの印刷オプション（MapPrintOptions）が等価であるかを判定する。
 *
 * @param a - 比較対象の印刷オプションA
 * @param b - 比較対象の印刷オプションB
 * @returns 等価であれば true、異なれば false
 */
const arePrintOptionsEqual = (
  a?: MapPrintOptions,
  b?: MapPrintOptions
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.paperSize === b.paperSize &&
    a.orientation === b.orientation &&
    a.scaleDenominator === b.scaleDenominator &&
    a.dpi === b.dpi
  );
};

/**
 * 2つのエクスポート設定（MapExportRenderOptions）が等価であるかを判定する。
 *
 * @param a - 比較対象のエクスポート設定A
 * @param b - 比較対象のエクスポート設定B
 * @returns 等価であれば true、異なれば false
 */
const areExportOptionsEqual = (
  a: MapExportRenderOptions,
  b: MapExportRenderOptions
): boolean => {
  return (
    a.includeLayers === b.includeLayers &&
    a.includePins === b.includePins &&
    a.includeDrawnShapes === b.includeDrawnShapes &&
    a.title === b.title &&
    a.showScaleBar === b.showScaleBar &&
    arePrintOptionsEqual(a.printOptions, b.printOptions)
  );
};

/**
 * ゼンリン地図のオーバーレイ情報
 * ゼンリンレイヤーのみをエクスポート対象にするため、
 * どの画像がゼンリンのものかを識別するのに使用する
 */
type ZenrinImageOverlay = {
  bounds: LatLngBounds;
  url: string;
} | null;

/**
 * フックのオプション
 */
interface UseMapImageExportOptions {
  /** Leafletマップインスタンスへの参照 */
  mapRef: RefObject<Map | null>;
  /** ゼンリン地図オーバーレイの情報（エクスポート時にゼンリンレイヤーを識別するため） */
  zenrinImageOverlay: ZenrinImageOverlay;
  /** ダウンロード画像に追加のレイヤーを含めるかどうか */
  includeLayers?: boolean;
  /** ダウンロード画像にピンを含めるかどうか */
  includePins?: boolean;
  /** ダウンロード画像に描画した図形を含めるかどうか */
  includeDrawnShapes?: boolean;
  /** ダウンロード画像に現在地マーカーを含めるかどうか */
  includeCurrentLocation?: boolean;
}

/**
 * 地図画像のエクスポート機能を提供するカスタムフック
 *
 * Leaflet/MapLibreで表示している地図をCanvas経由でPNG/PDF形式でダウンロード可能にする。
 * 特にゼンリン地図レイヤーのみを抽出してエクスポートする機能を持つ。
 *
 * @param options - マップ参照とゼンリンオーバーレイ情報
 * @returns ダウンロード関数と処理中フラグ
 */

export const useMapImageExport = ({
  mapRef,
  zenrinImageOverlay,
  includeLayers = false,
  includePins = false,
  includeDrawnShapes = false,
  includeCurrentLocation = true,
}: UseMapImageExportOptions): {
  handleDownloadMapImage: (
    format: MapImageFormat,
    options?: MapExportRenderOptions
  ) => Promise<boolean>;
  handleCreateMapImagePreview: (
    mode: MapExportMode,
    options?: MapExportRenderOptions,
    highResolution?: boolean,
    force?: boolean
  ) => Promise<string | null>;
  handleDownloadMapPdf: (options?: MapExportRenderOptions) => Promise<boolean>;
  handleApplyPrintScaleToMap: (options?: MapExportRenderOptions) => boolean;
  mapImageDownloading: boolean;
  exportProgress: WaitForMapReadyProgress | null;
} => {
  // ダウンロード処理中かどうかを管理（UIでローディング表示などに使用）
  const [mapImageDownloading, setMapImageDownloading] = useState(false);
  // エクスポートの進捗状況を管理
  const [exportProgress, setExportProgress] =
    useState<WaitForMapReadyProgress | null>(null);

  // 地図出力用のCanvasをキャッシュするためのRef
  const lastCanvasRef = useRef<{
    canvas: HTMLCanvasElement;
    mode: MapExportMode;
    options: MapExportRenderOptions;
    highResolution: boolean;
    center: LatLng;
    zoom: number;
    width: number;
    height: number;
  } | null>(null);

  // アンマウント時にキャッシュされたCanvasオブジェクトのメモリを解放する
  useEffect(() => {
    return () => {
      if (lastCanvasRef.current) {
        lastCanvasRef.current.canvas.width = 0;
        lastCanvasRef.current.canvas.height = 0;
        lastCanvasRef.current = null;
      }
    };
  }, []);

  /**
   * キャッシュされたCanvasオブジェクトのメモリを解放する。
   * マップイベントや描画イベントなどを検知した際に呼び出す。
   */
  const clearCanvasCache = useCallback(() => {
    if (lastCanvasRef.current) {
      lastCanvasRef.current.canvas.width = 0;
      lastCanvasRef.current.canvas.height = 0;
      lastCanvasRef.current = null;
    }
  }, []);

  /**
   * 地図変更イベント（レイヤー追加・削除、図形描画・編集など）を検知して、
   * キャッシュされたCanvasオブジェクトをクリアするためのリスナーを設定する。
   *
   * @param mapInstance - 対象のLeafletマップインスタンス
   */
  const setupMapListeners = useCallback(
    (mapInstance: Map) => {
      mapInstance.off("layeradd layerremove", clearCanvasCache);
      mapInstance.off(
        "draw:created draw:edited draw:deleted",
        clearCanvasCache
      );
      mapInstance.off(
        "pm:create pm:edit pm:remove pm:change pm:update",
        clearCanvasCache
      );

      mapInstance.on("layeradd layerremove", clearCanvasCache);
      mapInstance.on("draw:created draw:edited draw:deleted", clearCanvasCache);
      mapInstance.on(
        "pm:create pm:edit pm:remove pm:change pm:update",
        clearCanvasCache
      );
    },
    [clearCanvasCache]
  );

  /**
   * 呼び出し側から渡された一時的な描画オプションと、フック初期化時のデフォルト設定値をマージして、
   * 完全なエクスポート設定（ResolvedMapExportOptions）に解決する。
   *
   * @param options - ユーザー操作によって一時的に指定されたエクスポート描画設定
   * @returns デフォルト値が補完された解決済みのエクスポート設定
   */
  const resolveExportOptions = useCallback(
    (options?: MapExportRenderOptions): ResolvedMapExportOptions => ({
      includeLayers: options?.includeLayers ?? includeLayers,
      includePins: options?.includePins ?? includePins,
      includeDrawnShapes: options?.includeDrawnShapes ?? includeDrawnShapes,
      includeCurrentLocation:
        options?.includeCurrentLocation ?? includeCurrentLocation,
      title: options?.title,
      showScaleBar: options?.showScaleBar,
      printOptions: options?.printOptions,
    }),
    [includeCurrentLocation, includeDrawnShapes, includeLayers, includePins]
  );

  /**
   * 印刷用の縮尺設定（scaleDenominator）を元に、地図の表示範囲（Bounds）と拡大レベル（Zoom）を調整する。
   *
   * @param options - 縮尺設定を含む一時的なエクスポート設定
   * @returns 縮尺適用が成功した場合は true、マップ未準備や縮尺指定なしの場合は false
   */
  const handleApplyPrintScaleToMap = useCallback(
    (options?: MapExportRenderOptions): boolean => {
      const mapInstance = mapRef.current;
      const exportOptions = resolveExportOptions(options);
      const printOptions = exportOptions.printOptions;

      if (!mapInstance || !printOptions?.scaleDenominator) {
        return false;
      }

      const layout = getPrintLayout(printOptions);
      const center = mapInstance.getCenter();
      const targetBounds = createBoundsFromCenterMeters(
        center,
        (layout.mapWidthMm / 1000) * printOptions.scaleDenominator,
        (layout.mapHeightMm / 1000) * printOptions.scaleDenominator
      );

      mapInstance.fitBounds(targetBounds, {
        animate: false,
        padding: [0, 0],
      });
      mapInstance.invalidateSize(false);
      mapInstance.eachLayer((layer) => {
        const glMap = getGlMap(layer);
        if (glMap) {
          glMap.triggerRepaint();
          glMap._update();
        }
      });
      return true;
    },
    [mapRef, resolveExportOptions]
  );

  /**
   * 地図画像をダウンロードするメイン関数
   *
   * ゼンリン地図レイヤーのみを抽出してPNG/PDF形式でダウンロードする。
   * 他のレイヤー（マーカー、ポップアップ等）は一時的に非表示にして
   * ゼンリンのタイルとオーバーレイのみをキャプチャする。
   */
  const handleDownloadMapImage = useCallback(
    async (
      outputFormat: MapImageFormat,
      options?: MapExportRenderOptions
    ): Promise<boolean> => {
      const mapInstance = mapRef.current;
      const exportOptions = resolveExportOptions(options);

      if (!mapInstance) {
        toast.error("地図を準備中です。少し待ってからお試しください。");
        return false;
      }

      setupMapListeners(mapInstance);

      // 非表示にした要素を元に戻すための関数を積むスタック
      // エラーが発生しても確実に元に戻せるようにfinally節で使用
      const restoreDisplayStack: Array<() => void> = [];
      let succeeded = false;

      /**
       * 要素を一時的に非表示にし、復元関数をスタックに追加
       * 元のdisplay値を記憶して、後で正確に復元できるようにする
       */
      const hideElement = (el: HTMLElement | SVGElement): void => {
        const prevDisplay = el.style.display;
        el.style.display = "none";
        restoreDisplayStack.push(() => {
          el.style.display = prevDisplay;
        });
      };

      // ダウンロード中フラグを立てる（UIでローディング表示に使用）
      setMapImageDownloading(true);
      try {
        setExportProgress({
          totalTiles: 0,
          loadedTiles: 0,
          loadingOverlay: false,
          loadingLayer: false,
        });
        const container = mapInstance.getContainer();
        const currentWidth = container.clientWidth;
        const currentHeight = container.clientHeight;

        let outputCanvas: HTMLCanvasElement;
        const cache = lastCanvasRef.current;
        if (
          cache &&
          cache.mode === "image" &&
          areExportOptionsEqual(cache.options, exportOptions) &&
          cache.center.equals(mapInstance.getCenter()) &&
          cache.zoom === mapInstance.getZoom() &&
          cache.width === currentWidth &&
          cache.height === currentHeight
        ) {
          outputCanvas = cache.canvas;
        } else {
          // 非表示にするべきレイヤーを一時的に非表示にする。
          // 出力対象外レイヤーの判定ロジックは utils/mapExportVisibility に集約。
          applyExportVisibility({
            mapInstance,
            exportOptions: {
              includeLayers: exportOptions.includeLayers,
              includePins: exportOptions.includePins,
              includeDrawnShapes: exportOptions.includeDrawnShapes,
              includeCurrentLocation: exportOptions.includeCurrentLocation,
            },
            zenrinImageOverlayUrl: zenrinImageOverlay?.url,
            hideElement,
          });
          // ゼンリンタイルの取得失敗は display:none で「読み込み済」扱いになるため、
          // ここでカウントを開始しキャプチャ後に検査する。
          beginZenrinTileFailureWindow();
          // レイヤーの描画が完了してからCanvas化する。
          // タイムアウト時は旧 waitForLayersReady と同等のユーザー向けトースト
          // を出して、再ダウンロードを促す。
          await waitForMapReady(mapInstance, {
            // 並列フェッチ制限 + 429 リトライで最大 ~25 秒かかる可能性があるため、
            // デフォルトの 10s では足りない。30s まで延長する。
            // 高解像度の地図をダウンロードする場合30sでは足りないので60sに変更
            maxTimeoutMs: 60_000,
            onTimeout: () => {
              toast.error(
                "地図の読み込みに時間がかかっています。ダウンロードした画像が真っ白の場合はもう一度ダウンロードをお試しください",
                {
                  autoClose: false,
                  toastId: "map-load-timeout",
                }
              );
            },
            onProgress: (progress) => {
              setExportProgress(progress);
            },
          });
          const tileFailureCount = getZenrinTileFailureCount();
          if (tileFailureCount > 0) {
            // 部分的に白タイルが入った成果物を保存してしまわないよう、ダウンロード
            // 自体を中断する。Leaflet 側に残った失敗タイルキャッシュを破棄して、
            // ユーザが再度ダウンロードを試みた際に新しいフェッチが走るようにする。
            reloadZenrinTileLayers(mapInstance);
            toast.error(
              `地図タイルの読み込みに失敗しました（${tileFailureCount} 枚）。ネットワークを確認のうえ、もう一度ダウンロードをお試しください。`
            );
            return false;
          }
          // 地図をCanvasにレンダリング
          const { canvas, skippedSources, skippedCanvases } =
            await createMapCanvas(mapInstance);
          outputCanvas = composeImageCanvas(
            canvas,
            exportOptions.title,
            exportOptions.showScaleBar,
            mapInstance
          );

          // スキップされたレイヤーがあれば警告を表示
          // ユーザーに一部のコンテンツが含まれていないことを知らせる
          if (skippedSources.length > 0 || skippedCanvases.length > 0) {
            toast.warn(
              [
                skippedSources.length > 0
                  ? `一部のレイヤーはCORS制約のため含められませんでした: ${skippedSources.join(
                      ", "
                    )}`
                  : null,
                skippedCanvases.length > 0
                  ? `一部のベクター/キャンバスレイヤーを描画できませんでした: ${skippedCanvases.join(
                      ", "
                    )}`
                  : null,
              ]
                .filter(Boolean)
                .join(" / ")
            );
          }

          // キャッシュを更新
          lastCanvasRef.current = {
            canvas: outputCanvas,
            mode: "image",
            options: exportOptions,
            highResolution: false,
            center: mapInstance.getCenter(),
            zoom: mapInstance.getZoom(),
            width: currentWidth,
            height: currentHeight,
          };
        }
        // ファイル名用のタイムスタンプ（例: 20240115_143052）
        const timestamp = format(new Date(), "yyyyMMdd_HHmmss");

        // CanvasからPNG Blobを生成
        const pngBlob = await new Promise<Blob | null>((resolve) => {
          outputCanvas.toBlob(resolve);
        });

        if (!pngBlob || pngBlob.size < 1000) {
          throw new Error(
            "画像の生成に失敗しました。解像度を下げてお試しください。"
          );
        }

        /**
         * CanvasからPDF Blobを生成
         * jsPDFを使用してPNGをPDFに埋め込む
         */
        const createPdfBlob = (): Blob => {
          const imgData = outputCanvas.toDataURL("image/jpeg", 1.0);
          // 画像の縦横比に合わせて用紙の向きを決定
          // eslint-disable-next-line new-cap
          const pdf = new jsPDF({
            orientation:
              outputCanvas.width > outputCanvas.height
                ? "landscape"
                : "portrait",
            unit: "px",
            format: [outputCanvas.width, outputCanvas.height],
          });
          pdf.addImage(
            imgData,
            "JPEG",
            0,
            0,
            outputCanvas.width,
            outputCanvas.height
          );
          return pdf.output("blob");
        };

        // 出力フォーマットに応じたダウンロード処理
        if (outputFormat === "both") {
          // PNG + PDF をZIPにまとめてダウンロード
          const zip = new JSZip();
          zip.file(`map_${timestamp}.png`, pngBlob);
          zip.file(`map_${timestamp}.pdf`, createPdfBlob());
          const zipBlob = await zip.generateAsync({ type: "blob" });
          const url = URL.createObjectURL(zipBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `map_${timestamp}.zip`;
          link.click();
          // メモリリークを防ぐためBlobURLを解放
          URL.revokeObjectURL(url);
        } else if (outputFormat === "pdf") {
          // PDFのみダウンロード
          const pdfBlob = createPdfBlob();
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `map_${timestamp}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
        } else {
          // PNGのみダウンロード
          const url = URL.createObjectURL(pngBlob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `map_${timestamp}.png`;
          link.click();
          URL.revokeObjectURL(url);
        }
        toast.success("地図画像をダウンロードしました");
        succeeded = true;
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast.error("地図のダウンロードに失敗しました");
      } finally {
        // 非表示にした要素を逆順で復元
        // スタックから取り出すことで、追加した逆順で復元される
        while (restoreDisplayStack.length > 0) {
          const restore = restoreDisplayStack.pop();
          restore?.();
        }
        // maplibre glのレイヤーを強制描画する
        // こうすることで元の座標に戻った時に、maplibreレイヤーが描画されない問題に対処している
        mapInstance.eachLayer((layer) => {
          const glMap = getGlMap(layer);
          if (glMap) {
            glMap.triggerRepaint();
            glMap._update();
          }
        });
        setMapImageDownloading(false);
        setExportProgress(null);
      }
      return succeeded;
    },
    [
      mapRef,
      zenrinImageOverlay,
      resolveExportOptions,
      includePins,
      includeDrawnShapes,
      includeLayers,
      includeCurrentLocation,
    ]
  );

  /**
   * エクスポート用のCanvasを描画・生成する。
   * 指定したモード（画像/印刷）やオプションに基づき、一時的に地図サイズを変更して
   * キャプチャ用Canvasを作成し、合成・クロップ処理を行う。
   * メモリ効率化のため、同一の条件で再キャプチャが行われた場合はキャッシュを返却する。
   *
   * @param mode - エクスポートモード（'image' または 'print'）
   * @param options - 一時的なエクスポートオプション
   * @param highResolution - 印刷時に高解像度（200DPI相当）でレンダリングするかどうか
   * @param force - キャッシュを無視して強制的に再レンダリングするかどうか
   * @returns 生成された HTMLCanvasElement、もしくは失敗した場合は null
   */
  const renderMapExportCanvas = useCallback(
    async (
      mode: MapExportMode,
      options?: MapExportRenderOptions,
      // 印刷モード時にコンテナを 200dpi 相当まで一時拡大してキャプチャするかどうか。
      // プレビュー生成では false にして高速化、PDF保存時のみ true で高品質にする。
      highResolution = false,
      force = false
    ): Promise<HTMLCanvasElement | null> => {
      const mapInstance = mapRef.current;
      const exportOptions = resolveExportOptions(options);
      if (!mapInstance) {
        toast.error("地図を準備中です。少し待ってからお試しください。");
        return null;
      }

      // 印刷用DPI制限の適用（ブラウザクラッシュや画像破損の防止）
      if (mode === "print" && exportOptions.printOptions) {
        const targetDpi = highResolution ? exportOptions.printOptions.dpi : 300;

        if (
          checkCanvasSizeExceeded(
            exportOptions.printOptions.paperSize,
            exportOptions.printOptions.orientation,
            targetDpi
          )
        ) {
          toast.error(
            `指定された用紙サイズと解像度の組み合わせ (${targetDpi}DPI) は、ブラウザの制限（メモリ上限）を超えているため出力できません。解像度を下げてお試しください。`,
            { toastId: "dpi-exceeded-error" }
          );
          return null;
        }
      }

      setupMapListeners(mapInstance);

      const container = mapInstance.getContainer();
      const currentWidth = container.clientWidth;
      const currentHeight = container.clientHeight;

      const cache = lastCanvasRef.current;
      if (
        !force &&
        cache &&
        cache.mode === mode &&
        areExportOptionsEqual(cache.options, exportOptions) &&
        cache.highResolution === highResolution &&
        cache.center.equals(mapInstance.getCenter()) &&
        cache.zoom === mapInstance.getZoom() &&
        cache.width === currentWidth &&
        cache.height === currentHeight
      ) {
        return cache.canvas;
      }

      const restoreDisplayStack: Array<() => void> = [];
      let targetPrintBounds: LatLngBounds | null = null;
      let containerSizeState: MapContainerSizeState | null = null;
      const isHighResolutionPrint =
        mode === "print" &&
        highResolution &&
        exportOptions.printOptions !== undefined;
      const captureDevicePixelRatio =
        typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      let printStyleSession: PrintStyleSession | null = null;
      let printRenderContext: PrintRenderContext | null = null;
      let completedCanvas: HTMLCanvasElement | null = null;

      const hideElement = (el: HTMLElement | SVGElement): void => {
        const prevDisplay = el.style.display;
        el.style.display = "none";
        restoreDisplayStack.push(() => {
          el.style.display = prevDisplay;
        });
      };

      try {
        if (isHighResolutionPrint) {
          printStyleSession = createPrintStyleSession(mapInstance, {
            includeRegisteredAdapters: exportOptions.includeLayers,
            extraFactories: [
              () =>
                createLeafletPrintAdapter({
                  map: mapInstance,
                  includePins: exportOptions.includePins,
                  includeDrawnShapes: exportOptions.includeDrawnShapes,
                  drawnShapesPaneName: DRAWN_SHAPES_PANE_NAME,
                }),
            ],
          });
        }

        setExportProgress({
          totalTiles: 0,
          loadedTiles: 0,
          loadingOverlay: false,
          loadingLayer: false,
        });
        // 印刷モードかつ高解像度フラグが立っているときだけ、キャプチャ前に
        // コンテナを 200dpi 相当まで一時拡大する。プレビューでは行わないことで
        // タイル再ロードコストを避けてレスポンスを保つ。
        if (highResolution && mode === "print" && exportOptions.printOptions) {
          const { widthCssPx, heightCssPx } = getPrintCaptureSize(
            exportOptions.printOptions,
            captureDevicePixelRatio
          );
          containerSizeState = enlargeMapContainerOffscreen(
            mapInstance,
            widthCssPx,
            heightCssPx
          );
        }

        if (mode === "print" && exportOptions.printOptions?.scaleDenominator) {
          const layout = getPrintLayout(exportOptions.printOptions);
          targetPrintBounds = createBoundsFromCenterMeters(
            mapInstance.getCenter(),
            (layout.mapWidthMm / 1000) *
              exportOptions.printOptions.scaleDenominator,
            (layout.mapHeightMm / 1000) *
              exportOptions.printOptions.scaleDenominator
          );
          mapInstance.fitBounds(targetPrintBounds, {
            animate: false,
            padding: [0, 0],
          });
          mapInstance.invalidateSize(false);

          if (printStyleSession) {
            const referenceCaptureSize = getPrintCaptureSize(
              { ...exportOptions.printOptions, dpi: 300 },
              captureDevicePixelRatio
            );
            printRenderContext = createPrintRenderContext({
              dpi: exportOptions.printOptions.dpi,
              referenceZoom: getBoundsZoomForSize(
                mapInstance,
                targetPrintBounds,
                {
                  width: referenceCaptureSize.widthCssPx,
                  height: referenceCaptureSize.heightCssPx,
                }
              ),
              captureZoom: mapInstance.getZoom(),
            });
          }
        }

        applyExportVisibility({
          mapInstance,
          exportOptions: {
            includeLayers: exportOptions.includeLayers,
            includePins: exportOptions.includePins,
            includeDrawnShapes: exportOptions.includeDrawnShapes,
            includeCurrentLocation: exportOptions.includeCurrentLocation,
          },
          zenrinImageOverlayUrl: zenrinImageOverlay?.url,
          hideElement,
        });
        if (printStyleSession && printRenderContext) {
          await printStyleSession.apply(printRenderContext);
        }
        // ゼンリンタイルの取得失敗はリトライ後に display:none + done(error) で
        // 黙って完了扱いになる（waitForMapReady を時短させるための既存挙動）。
        // この区間でカウントしておくことで「全タイル失敗で白いキャンバスを
        // 返してしまう」事態を後段で検知し、ユーザに再試行を促す。
        beginZenrinTileFailureWindow();
        // プレビュー/印刷経路ではタイムアウト時のトーストは出さない
        // （旧 waitForMapRender と同等の挙動）。
        // 並列フェッチ制限 + 429 リトライで最大 ~25 秒かかる可能性があるため、
        // デフォルトの 10s では足りない。30s まで延長する。
        // 高解像度の地図画像をダウンロードする時、30秒では足りないので60秒に延長する
        await waitForMapReady(mapInstance, {
          maxTimeoutMs: 60_000,
          onProgress: (progress) => {
            setExportProgress(progress);
          },
        });
        const tileFailureCount = getZenrinTileFailureCount();
        if (tileFailureCount > 0) {
          // 1 枚でも欠けたら部分白タイルになる。プレビューや PDF を成果物として
          // 返す前にエラーとして扱い、ユーザに再試行してもらう。
          // Leaflet 側に残った失敗タイルキャッシュをここで破棄しておき、
          // 次のキャプチャでは新しいフェッチが走るようにする。
          // （これをしないと再キャプチャしても同じ白タイルが返り続ける）
          reloadZenrinTileLayers(mapInstance);
          toast.error(
            `地図タイルの読み込みに失敗しました（${tileFailureCount} 枚）。ネットワークを確認のうえ、もう一度プレビュー更新をお試しください。`
          );
          return null;
        }
        const { canvas, skippedSources, skippedCanvases } =
          await createMapCanvas(mapInstance);
        if (skippedSources.length > 0 || skippedCanvases.length > 0) {
          toast.warn("一部のレイヤーはプレビューに含められませんでした。");
        }

        let finalCanvas: HTMLCanvasElement | null;
        if (mode === "print" && exportOptions.printOptions) {
          const mapCanvas = targetPrintBounds
            ? cropCanvasToBounds(canvas, mapInstance, targetPrintBounds)
            : canvas;
          finalCanvas = composePrintCanvas(
            mapCanvas,
            exportOptions.title,
            exportOptions.printOptions,
            exportOptions.showScaleBar ?? false,
            mapInstance,
            exportOptions.printOptions.dpi
          );
        } else {
          finalCanvas = composeImageCanvas(
            canvas,
            exportOptions.title,
            exportOptions.showScaleBar,
            mapInstance
          );
        }

        if (!finalCanvas) {
          throw new Error(
            "画像の合成に失敗しました。解像度を下げてお試しください。"
          );
        }

        completedCanvas = finalCanvas;

        return finalCanvas;
      } finally {
        await printStyleSession?.restore();
        while (restoreDisplayStack.length > 0) {
          restoreDisplayStack.pop()?.();
        }
        if (containerSizeState) {
          restoreMapContainerSize(mapInstance, containerSizeState);
          // 拡大後にコンテナで fitBounds していると、サイズを戻したときに
          // ズームが噛み合わずユーザーの見える領域が変わってしまう。
          // ダイアログが期待する「印刷範囲が画面に収まる」状態に戻すため
          // ここで同じ bounds に対して再 fitBounds する。
          if (targetPrintBounds) {
            mapInstance.fitBounds(targetPrintBounds, {
              animate: false,
              padding: [0, 0],
            });
            mapInstance.invalidateSize(false);
          }
        }
        if (completedCanvas) {
          lastCanvasRef.current = {
            canvas: completedCanvas,
            mode,
            options: exportOptions,
            highResolution,
            center: mapInstance.getCenter(),
            zoom: mapInstance.getZoom(),
            width: currentWidth,
            height: currentHeight,
          };
        }
        mapInstance.eachLayer((layer) => {
          const glMap = getGlMap(layer);
          if (glMap) {
            glMap.triggerRepaint();
            glMap._update();
          }
        });
        setExportProgress(null);
      }
    },
    [mapRef, resolveExportOptions, zenrinImageOverlay?.url]
  );

  /**
   * 地図画像のエクスポート用プレビュー画像を生成し、そのオブジェクトURLを返す。
   *
   * @param mode - エクスポートモード（'image' または 'print'）
   * @param options - 一時的なエクスポートオプション
   * @param highResolution - 印刷時に高解像度（200DPI相当）でレンダリングするかどうか
   * @param force - キャッシュを無視して強制的に再レンダリングするかどうか
   * @returns 生成されたプレビュー画像の Blob オブジェクトURL、もしくは失敗した場合は null
   */
  const handleCreateMapImagePreview = useCallback(
    async (
      mode: MapExportMode,
      options?: MapExportRenderOptions,
      // 拡大プレビュー（確認画面）からの呼び出し時のみ true。
      // 印刷モードのときコンテナを 200dpi 相当へ一時拡大してキャプチャする。
      highResolution = false,
      force = false
    ): Promise<string | null> => {
      setMapImageDownloading(true);
      try {
        const canvas = await renderMapExportCanvas(
          mode,
          options,
          highResolution,
          force
        );
        if (!canvas) return null;
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", 0.9);
        });
        if (!blob || blob.size < 1000) {
          throw new Error(
            "プレビュー画像の生成に失敗しました。解像度を下げてお試しください。"
          );
        }
        return URL.createObjectURL(blob);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast.error("地図プレビューの生成に失敗しました");
        return null;
      } finally {
        setMapImageDownloading(false);
      }
    },
    [renderMapExportCanvas]
  );

  /**
   * 地図を高解像度のPDF形式でダウンロードする。
   *
   * @param options - 印刷オプションを含むエクスポート設定
   * @returns ダウンロード処理が成功した場合は true、失敗した場合は false
   */
  const handleDownloadMapPdf = useCallback(
    async (options?: MapExportRenderOptions): Promise<boolean> => {
      const exportOptions = resolveExportOptions(options);
      if (!exportOptions.printOptions) {
        toast.error("印刷設定を選択してください。");
        return false;
      }
      if (!exportOptions.printOptions.scaleDenominator) {
        toast.error("縮尺を指定してください。");
        return false;
      }

      setMapImageDownloading(true);
      try {
        // PDF保存時のみ、コンテナを拡大して高解像度キャプチャを行う。
        const canvas = await renderMapExportCanvas(
          "print",
          exportOptions,
          true
        );
        if (!canvas) return false;
        const layout = getPrintLayout(exportOptions.printOptions);
        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        if (!imgData || imgData === "data:," || imgData.length < 1000) {
          throw new Error(
            "PDF生成用の画像データ出力に失敗しました。用紙サイズや解像度を下げてお試しください。"
          );
        }
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: exportOptions.printOptions.orientation,
          unit: "mm",
          format: exportOptions.printOptions.paperSize,
        });
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          0,
          layout.pageWidthMm,
          layout.pageHeightMm
        );
        const timestamp = format(new Date(), "yyyyMMdd_HHmmss");
        pdf.save(`map_${timestamp}.pdf`);
        toast.success("PDFをダウンロードしました");
        return true;
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        toast.error("PDFの作成に失敗しました");
        return false;
      } finally {
        setMapImageDownloading(false);
      }
    },
    [renderMapExportCanvas, resolveExportOptions]
  );

  return {
    handleDownloadMapImage,
    handleCreateMapImagePreview,
    handleDownloadMapPdf,
    handleApplyPrintScaleToMap,
    mapImageDownloading,
    exportProgress,
  };
};
